import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';
import {
  VALIDATION_RUN_TOTAL_STEPS,
  type ValidationRunPhase,
  type ValidationRunProgress,
  type ValidationRunProgressMetric,
} from './validationRunProgress';

export function buildBaseProgressMetrics(selectedValidationIds: number[]): ValidationRunProgressMetric[] {
  return [{ label: 'checks selected', value: String(selectedValidationIds.length) }];
}

export function buildValidationRunProgress(
  phase: ValidationRunPhase,
  currentStep: number,
  message: string,
  runStartedAt: number,
  detail?: string,
  metrics: ValidationRunProgressMetric[] = []
): ValidationRunProgress {
  return {
    phase,
    message,
    detail,
    currentStep,
    totalSteps: VALIDATION_RUN_TOTAL_STEPS,
    startedAt: runStartedAt,
    updatedAt: Date.now(),
    metrics,
  };
}

export function buildPreparingProgress(selectedCount: number, runStartedAt: number): ValidationRunProgress {
  const label = String(selectedCount);
  return buildValidationRunProgress(
    'preparing',
    1,
    'Preparing validation run',
    runStartedAt,
    `Preparing ${label} selected validation check${selectedCount === 1 ? '' : 's'} for the current Revit document.`,
    buildBaseProgressMetrics(Array.from({ length: selectedCount }, (_, i) => i))
  );
}

export function buildSyncingProgress(message: string, detail: string, runStartedAt: number): ValidationRunProgress {
  return buildValidationRunProgress(
    'syncing',
    2,
    message,
    runStartedAt,
    detail,
    [
      buildBaseProgressMetrics([])[0],
      { label: 'request path', value: 'SaveMappings' },
    ]
  );
}

export function buildSubmittingProgress(selectedCount: number, runStartedAt: number): ValidationRunProgress {
  const label = String(selectedCount);
  return buildValidationRunProgress(
    'submitting',
    3,
    'Submitting validation request',
    runStartedAt,
    `Sending ${label} validation check${selectedCount === 1 ? '' : 's'} to the Revit validation runtime.`,
    buildBaseProgressMetrics(Array.from({ length: selectedCount }, (_, i) => i))
  );
}

export function buildRunningProgress(
  message: string,
  detail: string,
  runStartedAt: number,
  metrics: ValidationRunProgressMetric[] = []
): ValidationRunProgress {
  return buildValidationRunProgress(
    'running',
    4,
    message,
    runStartedAt,
    detail,
    metrics
  );
}

export function buildProcessingProgress(
  message: string,
  detail: string,
  runStartedAt: number
): ValidationRunProgress {
  return buildValidationRunProgress(
    'processing',
    5,
    message,
    runStartedAt,
    detail
  );
}

export function buildCompleteProgress(
  totalChecks: number,
  totalIssues: number,
  runStartedAt: number,
  baseMetrics: ValidationRunProgressMetric[]
): ValidationRunProgress {
  return buildValidationRunProgress(
    'complete',
    VALIDATION_RUN_TOTAL_STEPS,
    'Validation report ready',
    runStartedAt,
    `Completed ${totalChecks} checks with ${totalIssues} issue${totalIssues === 1 ? '' : 's'}.`,
    [
      ...baseMetrics,
      { label: 'checks returned', value: String(totalChecks) },
      { label: 'issues returned', value: String(totalIssues) },
    ]
  );
}

export function buildCacheHitProgress(
  runStartedAt: number,
  baseMetrics: ValidationRunProgressMetric[]
): ValidationRunProgress {
  return {
    phase: 'cache-hit',
    message: 'Reused cached validation report',
    detail: 'The model, selected checks, and mapping signature have not changed.',
    currentStep: VALIDATION_RUN_TOTAL_STEPS,
    totalSteps: VALIDATION_RUN_TOTAL_STEPS,
    startedAt: runStartedAt,
    updatedAt: Date.now(),
    metrics: baseMetrics,
  };
}

export function buildErrorProgress(
  message: string,
  detail: string,
  runStartedAt: number,
  baseMetrics: ValidationRunProgressMetric[]
): ValidationRunProgress {
  return buildValidationRunProgress('error', 4, message, runStartedAt, detail, baseMetrics);
}

export function mapHostProgressToRunProgress(
  progress: RevitOperationProgressSnapshot,
  runStartedAt: number,
  baseMetrics: ValidationRunProgressMetric[]
): ValidationRunProgress {
  const normalizedPhase = progress.phase.toLowerCase();
  const phase: ValidationRunPhase = progress.failed
    ? 'error'
    : normalizedPhase.includes('building') || progress.resultReady
      ? 'processing'
      : 'running';
  const currentStep = phase === 'processing' ? 5 : 4;

  return {
    operationId: progress.operationId,
    phase,
    message: progress.message || 'Running validation in Revit',
    detail: progress.detail,
    currentStep,
    totalSteps: VALIDATION_RUN_TOTAL_STEPS,
    startedAt: runStartedAt,
    updatedAt: Date.now(),
    state: progress.state,
    waitReason: progress.waitReason,
    waitElapsedMs: progress.waitElapsedMs ?? null,
    activeElapsedMs: progress.activeElapsedMs ?? null,
    processed: progress.processed ?? null,
    total: progress.total ?? null,
    percent: progress.percent ?? null,
    elapsedMs: progress.elapsedMs ?? null,
    etaMs: progress.etaMs ?? null,
    resultReady: progress.resultReady,
    error: progress.error,
    metrics: [
      ...baseMetrics,
      ...(typeof progress.issuesFound === 'number'
        ? [{ label: 'issues found', value: String(progress.issuesFound) }]
        : []),
    ],
  };
}

export function mapMappingSyncProgressToRunProgress(
  progress: RevitOperationProgressSnapshot,
  runStartedAt: number,
  baseMetrics: ValidationRunProgressMetric[]
): ValidationRunProgress {
  return {
    operationId: progress.operationId,
    phase: progress.failed ? 'error' : 'syncing',
    message: progress.message || 'Synchronizing validation mappings',
    detail: progress.waitReason || progress.currentItem || progress.detail,
    currentStep: 2,
    totalSteps: VALIDATION_RUN_TOTAL_STEPS,
    startedAt: runStartedAt,
    updatedAt: Date.now(),
    state: progress.state,
    waitReason: progress.waitReason,
    waitElapsedMs: progress.waitElapsedMs ?? null,
    activeElapsedMs: progress.activeElapsedMs ?? null,
    processed: progress.processed ?? null,
    total: progress.total ?? null,
    percent: progress.percent ?? null,
    elapsedMs: progress.elapsedMs ?? null,
    etaMs: progress.etaMs ?? null,
    resultReady: progress.resultReady,
    error: progress.error,
    metrics: [...baseMetrics, { label: 'request path', value: 'SaveMappings' }],
  };
}
